<script setup lang="ts">
import type { ExperimentalUI } from 'shared/src'
import { VoiceIsolationModel } from 'shared/src'
import { computed, ref, watch } from 'vue'
import TrashIcon from '@/assets/img/svg/icons/trash.svg?component'
import { useValidations } from '@/composables/api/useValidations'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags.ts'
import { isNotBlank } from '@/utils/String'
import { OHelperText } from '@ai/ui-components'
import {
  getTranscriptionModelOptions,
  getGenerationModelOptions,
  getVoiceIsolationModelOptions,
  flagSwitches,
  toolSwitches,
  MODEL_SAMPLING_DEFAULTS,
  GENERATION_MODEL_V2,
  type GenerationModel,
} from '@/composables/configurator/useExperimentalOptions.ts'
import { getVariableTypeOptions } from '@/composables/variables/variableTypeOptions'
import { DEFAULT_MAIN_SKILLS } from '@/composables/configurator/useVirtualAgentMainSkills'
import { useAnalytics } from '@/composables/analytics/useAnalytics'

const { t } = useI18n()
const { visibleErrors } = useValidations()
const voiceIsolationModelOptions = computed(() => getVoiceIsolationModelOptions(t))
const fieldTypeOptions = computed(() => getVariableTypeOptions(t))
const {
  showAgentOverride,
  showFeatureToggles,
  showContextualPromptOverride,
  showAdditionalParameters,
  showPersonaOverride,
  showArabicFeatures,
} = useFeatureFlags()
const { track } = useAnalytics()

const showArabic = computed(() =>
  typeof showArabicFeatures === 'boolean' ? showArabicFeatures : showArabicFeatures.value,
)
const computedGenerationModelOptions = computed(() => getGenerationModelOptions(showArabic.value))
const computedTranscriptionModelOptions = computed(() =>
  getTranscriptionModelOptions(showArabic.value),
)

const modelValue = defineModel<ExperimentalUI>({
  required: true,
})

const standardSkillsBeforeCustom = ref<
  Pick<ExperimentalUI['tools'], 'qualify_call' | 'answer_with_rag' | 'step_validation'> | undefined
>()

const customSkills = computed({
  get: () => modelValue.value.tools.custom_skill,
  set: (enabled: boolean) => {
    if (enabled) {
      standardSkillsBeforeCustom.value = {
        qualify_call: modelValue.value.tools.qualify_call,
        answer_with_rag: modelValue.value.tools.answer_with_rag,
        step_validation: modelValue.value.tools.step_validation,
      }
      modelValue.value.tools.qualify_call = false
      modelValue.value.tools.answer_with_rag = false
      modelValue.value.tools.step_validation = false
    } else {
      Object.assign(
        modelValue.value.tools,
        standardSkillsBeforeCustom.value ?? {
          qualify_call: DEFAULT_MAIN_SKILLS.qualify_call,
          answer_with_rag: DEFAULT_MAIN_SKILLS.answer_with_rag,
          step_validation: DEFAULT_MAIN_SKILLS.step_validation,
        },
      )
    }

    modelValue.value.tools.custom_skill = enabled
  },
})

const runToolsJsonError = computed(() => {
  const val = modelValue.value.persona_run_tools_on_enter
  if (!isNotBlank(val)) return undefined
  try {
    JSON.parse(val)
    return undefined
  } catch {
    return t('experimental.persona_override.run_tools_invalid_json')
  }
})

const voiceIdsJsonError = computed(() => {
  const val = modelValue.value.persona_voice_ids_override
  if (!isNotBlank(val)) return undefined
  try {
    JSON.parse(val)
    return undefined
  } catch {
    return t('experimental.persona_override.voice_ids_invalid_json')
  }
})

const canAddField = computed(() => {
  const hasRoomForMore = modelValue.value.form_schema.length < 5
  const allRowsComplete = modelValue.value.form_schema.every(
    (field) => isNotBlank(field.name) && isNotBlank(field.description),
  )
  return hasRoomForMore && allRowsComplete
})

const canRemoveField = computed(() => modelValue.value.form_schema.length > 1)

const addField = () => {
  if (canAddField.value) {
    modelValue.value.form_schema.push({
      name: '',
      description: '',
      type: 'string',
    })
  }
}

const removeField = (index: number) => {
  modelValue.value.form_schema.splice(index, 1)
}

watch(
  [
    () => modelValue.value.persona_role_override,
    () => modelValue.value.persona_run_tools_on_enter,
    () => modelValue.value.persona_voice_ids_override,
  ],
  ([role, runTools, voiceIds]) => {
    if (
      (isNotBlank(role) || isNotBlank(runTools) || isNotBlank(voiceIds)) &&
      !modelValue.value.persona_uuid_override
    ) {
      modelValue.value.persona_uuid_override = crypto.randomUUID()
    }
  },
)

watch(
  () => modelValue.value.transcription_model,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      track('Transcription Model Changed', {
        modelName: newVal,
      })
    }
  },
)

const MAX_TOKENS_MIN = 500

watch(
  () => modelValue.value.sampling.generation_model,
  (model) => {
    const defaults = MODEL_SAMPLING_DEFAULTS[model as GenerationModel]
    modelValue.value.sampling.temperature = defaults.temperature
    modelValue.value.sampling.top_k = defaults.top_k
    modelValue.value.sampling.top_p = defaults.top_p
    modelValue.value.sampling.max_tokens = defaults.max_tokens
  },
)

watch(
  () => modelValue.value.sampling.max_tokens,
  (val) => {
    if (modelValue.value.sampling.generation_model !== GENERATION_MODEL_V2) return
    if (!val || isNaN(val) || val < MAX_TOKENS_MIN) {
      modelValue.value.sampling.max_tokens = MAX_TOKENS_MIN
    }
  },
)
</script>

<template>
  <div class="flex flex-col space-y-6">
    <!-- Disclaimer -->
    <div
      class="flex gap-s min-w-[200px] max-w-full md:max-w-[328px]"
      data-testid="experimental-disclaimer"
    >
      <UiIcon name="warning" variant="solid" class="text-primary-warning shrink-0" size="m" />
      <OHelperText state="warning" :text="t('experimental.disclaimer')" />
    </div>

    <!-- Transcription Section -->
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('experimental.transcription.title')"
      :subtitle="t('experimental.transcription.subtitle')"
    >
      <div class="col-span-2">
        <ODropdown
          v-model="modelValue.transcription_model"
          :label="t('experimental.transcription.model')"
          :options="computedTranscriptionModelOptions"
          dataTestid="transcription-model"
        />
      </div>
    </OCardAdvanced>

    <!-- Voice Activity Detection Section -->
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('experimental.vad.title')"
      :subtitle="t('experimental.vad.subtitle')"
    >
      <div class="col-span-2 flex flex-col space-y-m">
        <OInput
          v-model.number="modelValue.vad.min_speech_duration"
          :label="t('experimental.vad.min_speech_duration')"
          :tooltip-content="t('experimental.vad.min_speech_duration_tooltip')"
          type="number"
          :min="0"
          :step="0.01"
          :precision="2"
          size="M"
          :widthSize="2"
          dataTestid="vad-min-speech-duration"
        />

        <OInput
          v-model.number="modelValue.vad.activation_threshold"
          :label="t('experimental.vad.activation_threshold')"
          :tooltip-content="t('experimental.vad.activation_threshold_tooltip')"
          type="number"
          :min="0"
          :max="1"
          :step="0.01"
          :precision="2"
          size="M"
          :widthSize="2"
          dataTestid="vad-activation-threshold"
        />
      </div>
    </OCardAdvanced>

    <!-- Speech Interruptions Section -->
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('experimental.speech_interruptions.title')"
      :subtitle="t('experimental.speech_interruptions.subtitle')"
      data-testid="speech-interruptions-section"
    >
      <div class="col-span-2 flex flex-col space-y-4">
        <OSwitch
          v-model="modelValue.speech_interruptions.welcome"
          :label="t('experimental.speech_interruptions.welcome')"
          dataTestid="speech-interruption-welcome"
        />
        <OSwitch
          v-model="modelValue.speech_interruptions.wrap_up"
          :label="t('experimental.speech_interruptions.wrap_up')"
          dataTestid="speech-interruption-wrap-up"
        />
        <OSwitch
          v-model="modelValue.speech_interruptions.re_engagement"
          :label="t('experimental.speech_interruptions.re_engagement')"
          dataTestid="speech-interruption-re-engagement"
        />
        <OSwitch
          v-model="modelValue.speech_interruptions.max_duration"
          :label="t('experimental.speech_interruptions.max_duration')"
          dataTestid="speech-interruption-max-duration"
        />
      </div>
    </OCardAdvanced>

    <!-- Noise Cancellation Section -->
    <FormSection
      :title="t('experimental.noise_cancellation.title')"
      :subtitle="t('experimental.noise_cancellation.subtitle')"
      data-testid="noise-cancellation-section"
    >
      <template #title-extra>
        <OBadge color="notice" :content="t('beta')" variant="secondary" />
      </template>

      <div class="col-span-2">
        <ODropdown
          v-model="modelValue.voice_isolation_model"
          :label="t('experimental.noise_cancellation.model')"
          :tooltip-content="t('experimental.noise_cancellation.model_tooltip')"
          :options="voiceIsolationModelOptions"
          dataTestid="voice-isolation-model"
        />
      </div>
      <div
        v-if="
          modelValue.voice_isolation_model === VoiceIsolationModel.Dtln ||
          modelValue.voice_isolation_model === VoiceIsolationModel.DeepFilterNet3
        "
        class="col-span-2"
      >
        <OSlider
          class="experimental-llm-slider px-0!"
          v-model.number="modelValue.noise_suppression_strength"
          :label="t('experimental.noise_cancellation.strength')"
          :min="0.5"
          :max="0.9"
          :step="0.05"
          :stepper="false"
          dataTestid="noise-suppression-strength-slider"
        >
          <template #value-label="{ value }">{{ value.toFixed(2) }}</template>
        </OSlider>
      </div>
    </FormSection>

    <!-- Advanced Dialogue Logic Section -->
    <FormSection
      :title="t('experimental.advanced_dialogue_logic.title')"
      :subtitle="t('experimental.advanced_dialogue_logic.subtitle')"
      data-testid="advanced-dialogue-logic-section"
    >
      <template #title-extra>
        <OBadge color="notice" :content="t('beta')" variant="secondary" />
      </template>

      <div class="col-span-2 flex flex-col space-y-4">
        <OSwitch
          v-model="modelValue.fsm"
          :label="t('experimental.advanced_dialogue_logic.fsm')"
          dataTestid="advanced-dialogue-logic-fsm"
        />
      </div>
    </FormSection>

    <!-- Fields Section -->
    <FormSection
      :title="t('experimental.form_schema.title')"
      :subtitle="t('experimental.form_schema.subtitle')"
      data-testid="fields-section"
    >
      <template #title-extra>
        <OBadge color="notice" :content="t('beta')" variant="secondary" />
      </template>

      <div class="col-span-2 flex flex-col space-y-4">
        <div
          v-for="(field, index) in modelValue.form_schema"
          :key="index"
          class="flex items-start gap-3 p-4 border border-gray-200 rounded-xl"
          data-testid="field-card"
        >
          <div class="flex min-w-0 flex-1 flex-col gap-4">
            <div class="flex flex-col gap-4 md:flex-row" data-testid="field-primary-fields">
              <OInput
                v-model="field.name"
                :label="t('experimental.form_schema.name')"
                :placeholder="t('experimental.form_schema.name_placeholder')"
                required
                :error-message="visibleErrors.experimental?.[`form_schema.${index}.name`]"
                :data-error-for="`form_schema.${index}.name`"
                dataTestid="field-name"
                class="min-w-0 flex-1"
              />

              <ODropdown
                v-model="field.type"
                :label="t('experimental.form_schema.type')"
                :options="fieldTypeOptions"
                dataTestid="field-type"
                class="min-w-0 flex-1"
              />
            </div>

            <VariableMentionTextArea
              v-model="field.description"
              :label="t('experimental.form_schema.description')"
              :placeholder="t('experimental.form_schema.description_placeholder')"
              :tooltip-content="t('experimental.form_schema.description_tooltip')"
              required
              :error-message="visibleErrors.experimental?.[`form_schema.${index}.description`]"
              :data-error-for="`form_schema.${index}.description`"
              dataTestid="field-description"
            />
          </div>

          <button
            type="button"
            :aria-label="t('remove')"
            :disabled="!canRemoveField"
            data-testid="remove-field"
            class="mt-8.5 flex size-6 shrink-0 items-center justify-center rounded-full focus:outline-none focus:ring-6 focus:ring-(--state-brand-focus) active:ring-6 active:ring-(--state-brand-press)"
            :class="canRemoveField ? 'cursor-pointer' : 'cursor-not-allowed'"
            @click="removeField(index)"
          >
            <TrashIcon
              class="size-4"
              :class="
                canRemoveField ? 'text-neutral-1 hover:text-primary-error' : 'text-primary-disabled'
              "
            />
          </button>
        </div>

        <OButton
          variant="secondary"
          size="S"
          :label="t('experimental.form_schema.add')"
          :disabled="!canAddField"
          @click="addField"
          dataTestid="add-field"
          class="self-start"
        />
      </div>
    </FormSection>

    <!-- Tools Section -->
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('experimental.tools.title')"
      :subtitle="t('experimental.tools.subtitle')"
    >
      <div class="col-span-2 flex flex-col space-y-4">
        <OSwitch
          v-for="tool in toolSwitches"
          :key="tool.key"
          v-model="modelValue.tools[tool.key]"
          :label="t(`experimental.tools.${tool.key}`)"
          :dataTestid="tool.testId"
        />
      </div>
    </OCardAdvanced>

    <!-- Flags Section -->
    <FormSection
      :title="t('experimental.flags.title')"
      :subtitle="t('experimental.flags.subtitle')"
      data-testid="flags-section"
    >
      <template #title-extra>
        <OBadge color="notice" :content="t('beta')" variant="secondary" />
      </template>

      <div class="col-span-2 flex flex-col space-y-4">
        <OSwitch
          v-for="flag in flagSwitches"
          :key="flag.key"
          v-model="modelValue.flags.enabled[flag.key]"
          :label="t(`experimental.flags.${flag.key}`)"
          :dataTestid="flag.testId"
        />
      </div>
    </FormSection>

    <!-- LLM Capabilities Section -->
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('experimental.llm_capabilities.title')"
      :subtitle="t('experimental.llm_capabilities.subtitle')"
    >
      <div class="col-span-2 flex flex-col">
        <ODropdown
          v-model="modelValue.sampling.generation_model"
          :label="t('experimental.llm_capabilities.model')"
          :options="computedGenerationModelOptions"
          dataTestid="generation-model-select"
        />

        <OSlider
          class="experimental-llm-slider px-0! mt-m"
          v-model.number="modelValue.sampling.temperature"
          :label="t('experimental.llm_capabilities.temperature')"
          :tooltip-content="t('experimental.llm_capabilities.temperature_tooltip')"
          :min="0"
          :max="1"
          :step="0.01"
          :stepper="false"
          dataTestid="temperature-slider"
        >
          <template #value-label="{ value, max }">{{ value.toFixed(2) }}/{{ max }}%</template>
        </OSlider>

        <OSlider
          class="experimental-llm-slider px-0!"
          v-model.number="modelValue.sampling.top_k"
          :label="t('experimental.llm_capabilities.top_k')"
          :tooltip-content="t('experimental.llm_capabilities.top_k_tooltip')"
          :min="0"
          :max="100"
          :step="1"
          :stepper="false"
          dataTestid="top-k-slider"
        >
          <template #value-label="{ value, max }">{{ value.toFixed(0) }}/{{ max }}%</template>
        </OSlider>

        <OSlider
          class="experimental-llm-slider px-0!"
          v-model.number="modelValue.sampling.top_p"
          :label="t('experimental.llm_capabilities.top_p')"
          :tooltip-content="t('experimental.llm_capabilities.top_p_tooltip')"
          :min="0.8"
          :max="1"
          :step="0.05"
          :stepper="false"
          dataTestid="top-p-slider"
        >
          <template #value-label="{ value, max }">{{ value.toFixed(2) }}/{{ max }}%</template>
        </OSlider>

        <OInput
          v-if="modelValue.sampling.generation_model === GENERATION_MODEL_V2"
          class="pb-m"
          v-model.number="modelValue.sampling.max_tokens"
          :label="t('experimental.llm_capabilities.max_tokens')"
          :tooltip-content="t('experimental.llm_capabilities.max_tokens_tooltip')"
          type="number"
          :min="500"
          :max="4000"
          size="M"
          :widthSize="2"
          dataTestid="max-tokens"
        />

        <OInput
          v-model.number="modelValue.flags.detection_window"
          :label="t('experimental.llm_capabilities.detection_window')"
          :tooltip-content="t('experimental.llm_capabilities.detection_window_tooltip')"
          type="number"
          :min="0"
          :max="100"
          size="M"
          :widthSize="2"
          dataTestid="detection-window"
        />
      </div>
    </OCardAdvanced>

    <!-- Feature Toggles Section -->
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      v-if="showFeatureToggles"
      inline
      :title="t('experimental.feature_toggles.title')"
      :subtitle="t('experimental.feature_toggles.tooltip')"
    >
      <div class="col-span-2">
        <OMultiInput
          v-model="modelValue.feature_toggles"
          :add-label="t('experimental.feature_toggles.add')"
          add-mode="progressive"
          :fields="['key', 'value']"
          :placeholders="[
            t('experimental.feature_toggles.key_placeholder'),
            t('experimental.feature_toggles.value_placeholder'),
          ]"
          dataTestid="feature-toggles"
        />
      </div>
    </OCardAdvanced>

    <!-- Custom Skills Section -->
    <section
      v-if="
        showAgentOverride ||
        showPersonaOverride ||
        showContextualPromptOverride ||
        showAdditionalParameters
      "
      id="custom-skills"
      class="flex flex-col md:flex-row md:items-start md:space-x-6 space-y-3"
      data-testid="custom-skills-section"
    >
      <div class="flex flex-col space-y-s min-w-[200px] max-w-full md:max-w-[328px]">
        <div class="flex items-center gap-s">
          <span class="flex typography-body-1-medium select-none">{{
            t('experimental.custom_skills.title')
          }}</span>
          <OBadge color="notice" :content="t('beta')" variant="secondary" />
        </div>
        <div class="typography-body-2-long relative text-neutral-1">
          <p>{{ t('experimental.custom_skills.subtitle') }}</p>
        </div>
      </div>

      <div class="flex-1 min-w-0 flex flex-col space-y-l">
        <OCardAdvanced
          elevation="S"
          border-radius="XL"
          content-class="border border-neutral-5"
          data-testid="custom-skills-toggle-card"
        >
          <OSwitch
            v-model="customSkills"
            :label="t('experimental.custom_skills.toggle')"
            dataTestid="custom-skills-toggle"
          />
        </OCardAdvanced>

        <!-- Agent Override -->
        <OCardAdvanced
          elevation="S"
          border-radius="XL"
          content-class="border border-neutral-5"
          v-if="customSkills && showAgentOverride"
          data-testid="agent-override-card"
        >
          <div class="col-span-2">
            <OInput
              v-model="modelValue.class_identifier_override"
              :label="t('experimental.agent_override.title')"
              :tooltip-content="t('experimental.agent_override.subtitle')"
              :placeholder="t('experimental.agent_override.class_identifier_placeholder')"
              dataTestid="agent-override-class-identifier"
            />
          </div>
        </OCardAdvanced>

        <!-- Persona Override -->
        <OCardAdvanced
          elevation="S"
          border-radius="XL"
          content-class="border border-neutral-5"
          v-if="customSkills && showPersonaOverride"
          data-testid="persona-override-card"
        >
          <div class="col-span-2 flex flex-col space-y-m">
            <OTextArea
              v-model="modelValue.persona_role_override"
              :label="t('experimental.persona_override.role')"
              :placeholder="t('experimental.persona_override.role_placeholder')"
              dataTestid="persona-role-override"
            />
            <OTextArea
              v-model="modelValue.persona_run_tools_on_enter"
              :label="t('experimental.persona_override.run_tools_on_enter')"
              placeholder='{"tool_name": {"param": "value"}}'
              :error-message="runToolsJsonError"
              dataTestid="persona-run-tools-on-enter"
            />
            <OTextArea
              v-model="modelValue.persona_voice_ids_override"
              :label="t('experimental.persona_override.voice_ids')"
              placeholder='{"fr": {"model_id": "tts-1026-preview", "voice_id": "14ad5ee3-..."}}'
              :error-message="voiceIdsJsonError"
              dataTestid="persona-voice-ids-override"
            />
          </div>
        </OCardAdvanced>

        <!-- Contextual Prompt Override -->
        <OCardAdvanced
          elevation="S"
          border-radius="XL"
          content-class="border border-neutral-5"
          v-if="customSkills && showContextualPromptOverride"
          data-testid="contextual-prompt-override-card"
        >
          <div class="col-span-2">
            <VariableMentionTextArea
              v-model="modelValue.contextual_prompt_override"
              :label="t('experimental.contextual_prompt_override.title')"
              :tooltip-content="t('experimental.contextual_prompt_override.subtitle')"
              :placeholder="t('experimental.contextual_prompt_override.placeholder')"
              dataTestid="contextual-prompt-override"
            />
          </div>
        </OCardAdvanced>

        <!-- Additional Parameters -->
        <OCardAdvanced
          elevation="S"
          border-radius="XL"
          content-class="border border-neutral-5"
          v-if="customSkills && showAdditionalParameters"
          data-testid="additional-parameters-card"
        >
          <div class="col-span-2">
            <OMultiInput
              v-model="modelValue.additional_parameters"
              :label="t('experimental.additional_parameters.title')"
              :tooltip-content="t('experimental.additional_parameters.subtitle')"
              :add-label="t('experimental.additional_parameters.add')"
              add-mode="progressive"
              :fields="['key', 'value']"
              :placeholders="[
                t('experimental.additional_parameters.key_placeholder'),
                t('experimental.additional_parameters.value_placeholder'),
              ]"
              dataTestid="additional-parameters"
            />
          </div>
        </OCardAdvanced>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* TODO: remove once decided by design team
  Source: https://diabolocom.clickup.com/t/36183802/AI-9103?comment=90120210067374
 */
.experimental-llm-slider :deep([data-testid='slider-title']),
.experimental-llm-slider :deep([data-testid='slider-value']) {
  font-family: var(--typography-body-1-strong-font-family);
  font-size: var(--text-sm);
  font-weight: var(--typography-body-2-strong-font-weight);
  line-height: var(--text-sm--line-height);
  color: var(--alias-neutral-10);
}
</style>
